/**
 * File: src/pages/Dashboard/campaigns/campaign-detail/index.js
 * Description: Campaign detail page that lists messages, statuses, activities,
 *              and allows activity updates, notes, filtering, and export.
 * Dependencies: React, Ant Design, Redux, Router, i18n, XLSX, custom components, network utils.
 * Guidelines:
 *  - Keep UI strings translatable via i18n.
 *  - Add JSDoc to non-trivial helpers and event handlers.
 *  - Avoid deep prop drilling; use Redux state/actions when shared.
 * Created: 2025-09-23
 * Last Updated: 2025-09-23
 */
 
import React, { Component, useEffect, useLayoutEffect, useState } from 'react'
import Flex from 'components/shared-components/Flex';
import { Tabs, Form, Button, message, Input, Row, Col, Card, Spin, Modal, Select, Dropdown, Space, } from 'antd';
import { Progress, Tooltip, Tag } from 'antd';
import { Badge, Descriptions, Table } from 'antd';
import './phone.css';
import './table.css';
import { ClockCircleOutlined, SearchOutlined, TeamOutlined, RollbackOutlined, ArrowRightOutlined, UserDeleteOutlined, LoadingOutlined, ExportOutlined, WhatsAppOutlined, UserOutlined, CloseCircleOutlined, CheckCircleOutlined, DownOutlined, EditOutlined, CarryOutOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import network from 'network/network';
import Moment from 'react-moment';
import { DATETIME_FORMAT } from 'constants/DateConstant';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { selectCampaignCustomers } from 'utils/redux/actions/campaignActions';
import { connect } from 'react-redux';
import ReusableButton from 'components/Elements/ReusableButton';
import * as XLSX from 'xlsx';
import tools from 'tools'
import EllipsisDropdown from 'components/shared-components/EllipsisDropdown';
import EditCustomer from 'pages/Dashboard/campaigns/insertupdate-customer';
import DetailDashboardCustomer from 'pages/Dashboard/dashboard-customer/detail-customer';
import TextArea from 'antd/es/input/TextArea';


/**
 * CampaignsDetail Component
 *
 * Displays detailed campaign messages with delivery statuses, activities, and
 * customer information. Supports filtering, activity updates, adding notes,
 * and exporting to Excel.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.campaignDetail - Campaign details from Redux
 * @returns {JSX.Element}
 */
const CampaignsDetail = (props) => {

    const { t, i18n } = useTranslation();
    const { campaignDetail } = props;

    const location = useLocation();
    const { id } = useParams();
    const [dataSource, setDataSource] = useState();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [customerId, setCustomerId] = useState();
    const [group, setSelectGroup] = useState();
    const [selectedStatus, setSelectedStatus] = useState();
    const [storedSelectedApp, setStoredSelectedApp] = useState();
    const [modalParam, setModalParam] = useState();
    const [activities, setActivities] = useState([]);
    const [activityNote, setActivityNote] = useState();
    const [isNoteModal, setIsNoteModal] = useState(false);
    const [activity, setActivity] = useState();
    const [selectActivity, setSelectActivity] = useState();
    const navigate = useNavigate();
    const antIcon = <LoadingOutlined style={{ fontSize: 36, color: '#697780' }} spin />;

    /**
     * Handles search input changes and updates filter value.
     *
     * @param {Event} e - Input change event
     */
    const onSearch = e => {

        const value = e.currentTarget.value
        setSearchValue(value);
    }

    useEffect(() => {
        getCampaignsDetails();
        getActivities();
    }, [])
    // useEffect(() => {
    //     getActivities();
    // })

    /**
     * Fetches active activities for dropdown actions.
     *
     * @async
     */
    const getActivities = async () => {
        var params = {};
        var result = await network.get("/admin/Activity/getAllActivities", params, false);

        setActivities(result.data.data.filter(i => i.status));

    }

    /**
     * Persists note update for the selected activity and updates the table row.
     *
     * @async
     */
    const updateActivityNote = async () => {
        var params = {
            campaignid: activity.campaignId,
            mainmessageid: activity.mainMessageId,
            activitynote: activityNote

        }
        var result = await network.post("/admin/Activity/insertActivityStatus/", params, false);

        if (result.isSuccess) {
            message.success(t("activityChangeSuccess"));
            setIsNoteModal(false);

            const updatedRow = {
                ...activity,
                activityNote: activityNote
            };

            setDataSource(prev =>
                prev.map(row => row.id === activity.id ? updatedRow : row)
            );
            // getCampaignsDetails();
        } else {
            message.error(t("activityChangeError"))
        }
    }
    /**
     * Updates activity status for a given message row.
     *
     * @param {Object} record - Table row record
     * @param {Object} item - Selected activity item
     * @async
     */
    const handleActivity = async (record, item) => {

        var params = {
            campaignid: record.campaignId,
            mainmessageid: record.mainMessageId,
            activityid: item.activityid

        }
        var result = await network.post("/admin/Activity/insertActivityStatus/", params, false);

        if (result.isSuccess) {
            message.success(t("activityChangeSuccess"));

            const updatedRow = {
                ...record,
                activityId: item.activityid,
                activityDesc: item.activitydesc
            };

            setDataSource(prev =>
                prev.map(row => row.id === record.id ? updatedRow : row)
            );

        } else {
            message.error(t("activityChangeError"))
        }
    }

    /**
     * Builds activity dropdown menu items for a table row.
     *
     * @param {Object} record - Table row record
     * @returns {Array<{key:string|number,label:any,onClick:Function}>}
     */
    const dropdownActivities = (record) => {

        return activities.map((item) => ({
            key: item.activityid,
            label: item.activitydesc,
            onClick: () => handleActivity(record, item)
        }))
    }
    /**
     * Builds action dropdown menu for a table row (WhatsApp, customer modal, note).
     *
     * @param {Object} record - Table row record
     * @returns {Array}
     */
    const dropdownMenu = (record) => {
        return [
            {
                label: (
                    <a
                        href={`https://wa.me/${record.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {t('contactWhatsapp')}
                    </a>
                ),
                key: '3',
                icon: <WhatsAppOutlined />,
            },
            {
                label: t("showCustomer"),
                key: '4',
                icon: <UserOutlined />,
                onClick: () => {
                    // navigate(`/customer-edit/${record.id}`)


                    const SelectedApp = JSON.parse(localStorage.getItem('selectedApp'));

                    setStoredSelectedApp(SelectedApp);


                    if (SelectedApp.appName == "campaign"&&SelectedApp.appName=="agent") {

                        // navigate(`/customer-edit/${record.id}`)
                        setModalParam(record.id);
                        setIsModalVisible(true)


                    } else if (storedSelectedApp.appName == "coffeeproject") {
                        setCustomerId(record.id);
                        setIsModalVisible(true)
                    }
                },
            },
            {
                label: t("activityNote"),
                key: '5',
                icon: <EditOutlined />,
                onClick: () => {
                    setActivityNote(record.activityNote)
                    setActivity(record);
                    setIsNoteModal(true);
                }
            },

        ];
    };

    const menu = {
        items: activities,
        onClick: ({ key }) => console.log(`Clicked on item ${key}`),
    };
    const columns = [
        {

            dataIndex: 'campaignsName',
            key: 'campaignsName',
            width: '30px',
            colSpan: 1,
            align: 'center',
            render: (_, record) => (
                <div>
                    <span>
                        <img src='/img/avatars/user-blank-image.jpg' width={50}></img>
                    </span>

                </div>
            )
        },
        {
            title: t('customerName'),
            dataIndex: 'customerName',
            key: 'customerName',
            width: '350px',
            sorter: (a, b) => tools.antdTableSorter(a, b, "customerName"),
            render: (_, record) => (
                <div>
                    <div className="flex-container d-inline">
                        <div className="item  ">{record.customerName}</div>
                    </div>
                </div>
            )
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: '150px',
            // sorter: (a, b) => utils.antdTableSorter(a, b, 'campaignsName'),
            sorter: (a, b) => tools.antdTableSorter(a, b, "phone"),
            render: (_, record) => (
                <div>
                    <div className="flex-container d-inline">
                        <div className="item  ">{record.phone}</div>
                    </div>
                </div>
            )
        },

        {
            title: t('status'),
            dataIndex: 'id',
            width: 250,
            align: 'center',
            // sorter: (a, b) => utils.antdTableSorter(a, b, 'campaignsName'),
            render: (_, elm) => {
                return (
                    <Tooltip title={elm.statusType == "failed" ?
                        `
                    ${t("errorCode")} : ${elm.errorCode} \n
                    ${t("errorMessage")} : ${elm.errorDesc}
                    `
                        : ""} >
                        <div className='my-2 '>
                            {elm.statusType == "read" ? (
                                <><Tag color={"orange"}>{t('read')}</Tag></>
                            ) : elm.statusType == "sent" ? (
                                <><Tag color={"green"}>{t('sent')}</Tag></>
                            ) : elm.statusType == "delivered" ? (
                                <><Tag color={"yellow"}>{t('delivered')}</Tag> </>
                            ) : elm.statusType == "failed" ? (
                                <Tag color={"red"}>{t('failed')}</Tag>
                            ) : (
                                <><Tag color={"red"}>{t('notDelivered')}</Tag> </>
                            )}
                        </div>
                    </Tooltip>

                )
            }

        },
        {
            title: t('replybuttondesc'),
            dataIndex: 'replyButtonDesc',
            key: 'replyButtonDesc',
            width: '150px',
            // sorter: (a, b) => utils.antdTableSorter(a, b, 'campaignsName'),
            sorter: (a, b) => {
                if (!a.replyButtonDesc && !b.replyButtonDesc) return 0;
                if (!a.replyButtonDesc) return 1;
                if (!b.replyButtonDesc) return -1;
                return a.replyButtonDesc.localeCompare(b.replyButtonDesc);
            },
            render: (_, record) => (
                <div>
                    <div className="flex-container d-inline">
                        <div className="item  ">{record.replyButtonDesc}</div>
                    </div>
                </div>
            )
        },
        {
            title: t('statusDate'),
            dataIndex: 'id',
            width: 250,
            align: 'center',
            // sorter: (a, b) => utils.antdTableSorter(a, b, 'campaignsName'),
            sorter: (a, b) => tools.antdTableSorter(a, b, "createdDate"),
            render: (_, elm) => {
                return (
                    <div className='my-2'>
                        <div>{moment(elm.createdDate).format('MM-DD-YYYY')} {moment(elm.createdDate).format('HH:mm')}</div>
                        <div></div>
                    </div>
                )
            }

        },
        {
            title: t('activityStatus'),
            dataIndex: 'activityDesc',
            key: 'activityDesc',
            width: '20px',
            // sorter: (a, b) => utils.antdTableSorter(a, b, 'campaignsName'),
            render: (_, record) => (
                <div>
                    <div className="d-flex space-between">
                        <Tag color="blue" icon={<CarryOutOutlined />}>
                            {record.activityDesc ? record.activityDesc : t("notExist")}
                        </Tag>
                        <Dropdown menu={{ items: dropdownActivities(record) }}
                            trigger={['hover']}>
                            <a onClick={e => e.preventDefault()}> <DownOutlined /></a>
                        </Dropdown>
                    </div>
                </div>
            )
        },
        {
            title: t('cancelStatus'),
            dataIndex: 'isCancelledByUser',
            key: 'isCancelledByUser',
            width: '120px',
            sorter: (a, b) => tools.antdTableSorter(a, b, "isCancelledByUser"),
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {record.isCancelledByUser ? (
                        <Tooltip title={t("deniedByUser")}>
                            <CloseCircleOutlined style={{ fontSize: 23, color: '#b82a23' }} />
                        </Tooltip>
                    ) : (
                        <Tooltip title={t("exist")}>
                            <CheckCircleOutlined style={{ fontSize: 23, color: '#52c41a' }} />
                        </Tooltip>
                    )}
                </div>
            )
        }
        ,
        {
            dataIndex: "",
            render: (_, record) => (
                <div>
                    <EllipsisDropdown menu={{ items: dropdownMenu(record) }} />
                </div>
            )
        }

    ];

    /**
     * Loads campaign details into Redux and triggers table population.
     *
     * @async
     */
    const getCampaignsDetails = async () => {
        var params = {
            campaignId: id
        }
        props.selectCampaignCustomersAction(params);
    }


    useEffect(() => {
        if (campaignDetail?.campaignCustomer?.length > 0) {
            setDataSource(campaignDetail.campaignCustomer);
        }
    }, [campaignDetail]);


    var data = campaignDetail?.campaignCustomer?.length > 0 ? JSON.parse(JSON.stringify(campaignDetail?.campaignCustomer)) : null;

    /**
     * Applies client-side filtering by search text, status, activity, and group.
     */
    const filteredData = (dataSource || []).filter(item => {
        if (searchValue || selectedStatus || selectActivity || group) {

            const normalizeText = (text) =>
                text.toLocaleLowerCase('tr').normalize('NFKC');

            const isSearch = searchValue
                ? Object.values(item).some(value =>
                    normalizeText(String(value)).includes(normalizeText(searchValue))
                )
                : true;

            const isStatus = selectedStatus ? item.statusType == selectedStatus : true;

            const isActivityType = selectActivity ? item.activityId == selectActivity : true;

            const isGroup = group ? item.groupId == group : true;

            return isSearch && isStatus && isActivityType && isGroup;
        }
        return true; // Eğer filtreleme şartı yoksa tüm veriyi döndür
    });

    if (searchValue.length > 0) {
        data = data?.filter(function (el) {
            return (el?.customerName?.toUpperCase().indexOf(searchValue.toUpperCase()) !== -1 ||
                el?.phone?.toUpperCase().indexOf(searchValue.toUpperCase()) !== -1)
        });
    }

    /**
     * Calculates delivered percentage from campaign statistics.
     *
     * @returns {number|string} Percentage value (0-100)
     */
    const getDeliveredPercent = () => {
        const percent = campaignDetail?.campaignStatistics?.receiverCount == 0 ? 0 :
            Math.round(((campaignDetail?.campaignStatistics?.deliveredCount || 0) / (campaignDetail?.campaignStatistics?.receiverCount || 1)) * 100).toFixed(0) || 0;

        return percent;
    }

    /**
     * Maps statusType to localized display text.
     *
     * @param {Object} elm - Record with statusType
     * @returns {string}
     */
    const getStatusText = (elm) => {

        switch (elm.statusType) {
            case "read":
                return t('read');
            case "sent":
                return t('sent');
            case "delivered":
                return t('delivered');
            case "failed":
                return t('failed');

            default:
                return t('notDelivered');
        }



    }
    /**
     * Exports current filtered table data to an Excel file.
     */
    const handleExcelExport = () => {
        const excelData = campaignDetail?.campaignCustomer.map((record) => ({
            [t('customerName')]: record.customerName,
            [t('phoneNumber')]: record.phone,
            [t('status')]: getStatusText(record),
            [t('replybuttondesc')]: record.replyButtonDesc,
            [t('statusDate')]: moment(record.createdDate).format('MM-DD-YYYY'),
            [t('statusTime')]: moment(record.createdDate).format('HH:mm:ss'),
            [t('activityStatus')]:record.activityDesc ? record.activityDesc : t("notExist"),
            [t("cancelStatus")]: record.isCancelledByUser ? t("deniedByUser") : t("exist")

        }));

        // Create a new worksheet and add the data
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

        // Export to Excel
        XLSX.writeFile(workbook, "CustomerData.xlsx");
    }


    return (
        props.isLoading ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Spin indicator={antIcon} tip={t("dataLoading")} size="large" />
            </div>)
            : (
                <div>

                    <Card title={t('campaignStatistics')}>
                        <Row justify="space-between" gutter={[16, 16]}>
                            <Col flex="1" key={1}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1 ' >{t('recipient')}</h4>
                                        </Col>
                                        <Col span={4} >
                                            <TeamOutlined className='mt-1 ml-2 d-flex align-items-center justify-content-center' style={{ fontSize: '21px', color: "#52c41a" }} />
                                        </Col>
                                    </Row>
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.receiverCount || 0} </h1>
                                    <Row className='ml-1 mr-2'>
                                        <Col span={18}>
                                            <Progress strokeColor={"#52c41a"} className="mt-1 mr-1 mb-1 " percent={campaignDetail?.campaignStatistics?.receiverCount * 100 || 0} showInfo={false} />
                                        </Col>
                                        <Col span={6}>
                                            <h4 className='mt-1 ml-2'>%100</h4>
                                        </Col>
                                    </Row>
                                    {/* <Progress strokeColor={"#52c41a"} className=" mb-1 " percent={99} />*/}
                                </Card>
                            </Col>
                            <Col flex="1" key={2}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1 ' >{t('sent')}</h4>
                                        </Col>
                                        <Col span={4} >

                                            <h2 className='ml-3' style={{ color: "#697780", margin: 0, height: "33px", textAlign: 'center' }}><svg viewBox="0 0 12 11" height="19" width="19" preserveAspectRatio="xMidYMid meet" className="" fill="none"><title>msg-check</title><path d="M11.1549 0.652832C11.0745 0.585124 10.9729 0.55127 10.8502 0.55127C10.7021 0.55127 10.5751 0.610514 10.4693 0.729004L4.28038 8.36523L1.87461 6.09277C1.8323 6.04622 1.78151 6.01025 1.72227 5.98486C1.66303 5.95947 1.60166 5.94678 1.53819 5.94678C1.407 5.94678 1.29275 5.99544 1.19541 6.09277L0.884379 6.40381C0.79128 6.49268 0.744731 6.60482 0.744731 6.74023C0.744731 6.87565 0.79128 6.98991 0.884379 7.08301L3.88047 10.0791C4.02859 10.2145 4.19574 10.2822 4.38194 10.2822C4.48773 10.2822 4.58929 10.259 4.68663 10.2124C4.78396 10.1659 4.86436 10.1003 4.92784 10.0156L11.5738 1.59863C11.6458 1.5013 11.6817 1.40186 11.6817 1.30029C11.6817 1.14372 11.6183 1.01888 11.4913 0.925781L11.1549 0.652832Z" fill="currentcolor"></path></svg> </h2>
                                        </Col>
                                    </Row>
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.sendedCount || 0}</h1>
                                    <Row className='ml-1 mr-2'>
                                        <Col span={18}>
                                            <Progress strokeColor={"#697780"} className="mt-1 mr-1 mb-1 " percent={(campaignDetail?.campaignStatistics?.sendedCount / campaignDetail?.campaignStatistics?.receiverCount) * 100 || 0} showInfo={false} />
                                        </Col>
                                        <Col span={6}>
                                            <h4 className='mt-1 ml-2'>%{(campaignDetail?.campaignStatistics?.receiverCount || 0) == 0 ? 0 : Math.round((campaignDetail?.campaignStatistics?.sendedCount / (campaignDetail?.campaignStatistics?.receiverCount || 0)) * 100).toFixed(0) || 0}</h4>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col flex="1" key={3}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1 ' >{t('delivered')}</h4>
                                        </Col>
                                        <Col span={4} >

                                            <h2 className='ml-3' style={{ color: "#697780", margin: 0, height: "33px", textAlign: 'center' }}><svg viewBox="0 0 18 18" height="21" width="21" preserveAspectRatio="xMidYMid meet" className="dh5rsm73 hpdpob1j" version="1.1" x="0px" y="0px" enable-background=""><title>status-dblcheck</title><path fill="currentColor" d="M17.394,5.035l-0.57-0.444c-0.188-0.147-0.462-0.113-0.609,0.076l-6.39,8.198 c-0.147,0.188-0.406,0.206-0.577,0.039l-0.427-0.388c-0.171-0.167-0.431-0.15-0.578,0.038L7.792,13.13 c-0.147,0.188-0.128,0.478,0.043,0.645l1.575,1.51c0.171,0.167,0.43,0.149,0.577-0.039l7.483-9.602 C17.616,5.456,17.582,5.182,17.394,5.035z M12.502,5.035l-0.57-0.444c-0.188-0.147-0.462-0.113-0.609,0.076l-6.39,8.198 c-0.147,0.188-0.406,0.206-0.577,0.039l-2.614-2.556c-0.171-0.167-0.447-0.164-0.614,0.007l-0.505,0.516 c-0.167,0.171-0.164,0.447,0.007,0.614l3.887,3.8c0.171,0.167,0.43,0.149,0.577-0.039l7.483-9.602 C12.724,5.456,12.69,5.182,12.502,5.035z"></path></svg></h2>
                                        </Col>
                                    </Row >
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.deliveredCount || 0}</h1>
                                    <Row className='ml-1 mr-2' >
                                        <Col span={18}>
                                            <Progress strokeColor={"#697780"} className="mt-1 mr-1 mb-1 " percent={((campaignDetail?.campaignStatistics?.deliveredCount || 0) / campaignDetail?.campaignStatistics?.receiverCount || 0) * 100 || 0} showInfo={false} />
                                        </Col>
                                        <Col span={6}>
                                            <h4 className='mt-1 ml-2'>%{getDeliveredPercent()}</h4>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col flex="1" key={4}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1 ' >{t('read')} </h4>
                                        </Col>
                                        <Col span={4} >
                                            <h2 className='ml-3' style={{ color: "#53bdea", margin: 0, height: "33px", textAlign: 'center' }}><svg viewBox="0 0 18 18" height="21" width="21" preserveAspectRatio="xMidYMid meet" className="dh5rsm73 hpdpob1j" version="1.1" x="0px" y="0px" enable-background=""><title>status-dblcheck</title><path fill="currentColor" d="M17.394,5.035l-0.57-0.444c-0.188-0.147-0.462-0.113-0.609,0.076l-6.39,8.198 c-0.147,0.188-0.406,0.206-0.577,0.039l-0.427-0.388c-0.171-0.167-0.431-0.15-0.578,0.038L7.792,13.13 c-0.147,0.188-0.128,0.478,0.043,0.645l1.575,1.51c0.171,0.167,0.43,0.149,0.577-0.039l7.483-9.602 C17.616,5.456,17.582,5.182,17.394,5.035z M12.502,5.035l-0.57-0.444c-0.188-0.147-0.462-0.113-0.609,0.076l-6.39,8.198 c-0.147,0.188-0.406,0.206-0.577,0.039l-2.614-2.556c-0.171-0.167-0.447-0.164-0.614,0.007l-0.505,0.516 c-0.167,0.171-0.164,0.447,0.007,0.614l3.887,3.8c0.171,0.167,0.43,0.149,0.577-0.039l7.483-9.602 C12.724,5.456,12.69,5.182,12.502,5.035z"></path></svg></h2>

                                        </Col>
                                    </Row >
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.readedCount || 0
                                    }</h1>
                                    <Row className='ml-1 mr-2'>
                                        <Col span={18}>
                                            <Progress strokeColor={"#53bdea"} className="mt-1 mr-1 mb-1 " percent={
                                                ((campaignDetail?.campaignStatistics?.readedCount || 0) / campaignDetail?.campaignStatistics?.receiverCount || 0) * 100 || 0
                                            } showInfo={false} />
                                        </Col>
                                        <Col span={6}>
                                            <h4 className='mt-1 ml-2'>%{campaignDetail?.campaignStatistics?.receiverCount == 0 ? 0 : Math.round(((campaignDetail?.campaignStatistics?.readedCount || 0)
                                                / campaignDetail?.campaignStatistics?.receiverCount || 0) * 100).toFixed(0) || 0
                                            }</h4>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            <Col flex="1" key={4}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1 ' >{t('reply')} </h4>
                                        </Col>
                                        <Col span={4} >
                                            <h2 className='ml-3' style={{ color: "#53bdea", margin: 0, height: "33px", textAlign: 'center' }}>
                                                <svg width={"21"} height={"21"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#34B7F1" transform="matrix(1, 0, 0, 1, 0, 0)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.4800000000000001"></g><g id="SVGRepo_iconCarrier"> <path d="M20 17V15.8C20 14.1198 20 13.2798 19.673 12.638C19.3854 12.0735 18.9265 11.6146 18.362 11.327C17.7202 11 16.8802 11 15.2 11H4M4 11L8 7M4 11L8 15" stroke="#34B7F1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                                            </h2>

                                        </Col>
                                    </Row >
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.repliedCount || 0
                                    }</h1>
                                    <Row className='ml-1 mr-2'>
                                        <Col span={18}>
                                            <Progress strokeColor={"#53bdea"} className="mt-1 mr-1 mb-1 " percent={
                                                ((campaignDetail?.campaignStatistics?.repliedCount || 0) / campaignDetail?.campaignStatistics?.receiverCount || 0) * 100 || 0
                                            } showInfo={false} />
                                        </Col>
                                        <Col span={6}>
                                            <h4 className='mt-1 ml-2'>%{campaignDetail?.campaignStatistics?.receiverCount == 0 ? 0 : Math.round(((campaignDetail?.campaignStatistics?.repliedCount || 0)
                                                / campaignDetail?.campaignStatistics?.receiverCount || 0) * 100).toFixed(0) || 0
                                            }</h4>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            <Col flex="1" key={5}>
                                <Card bodyStyle={{ padding: 8, boxShadow: "5px 8px 24px 5px rgba(105, 119, 128, 0.1)" }} >
                                    <Row className='mr-1'>
                                        <Col span={19}><h4 className='ml-1' >{t('notDelivered')} </h4>
                                        </Col>
                                        <Col span={4} >
                                            <h2 className='ml-3' style={{ color: "red", margin: 0, height: "33px", textAlign: 'center' }}><svg viewBox="0 0 16 15" height="22" width="22" preserveAspectRatio="xMidYMid meet" className="" version="1.1" x="0px" y="0px" enable-background="new 0 0 16 15"><title>msg-time</title><path fill="currentColor" d="M9.75,7.713H8.244V5.359c0-0.276-0.224-0.5-0.5-0.5H7.65c-0.276,0-0.5,0.224-0.5,0.5v2.947 c0,0.276,0.224,0.5,0.5,0.5h0.094c0.001,0,0.002-0.001,0.003-0.001S7.749,8.807,7.75,8.807h2c0.276,0,0.5-0.224,0.5-0.5V8.213 C10.25,7.937,10.026,7.713,9.75,7.713z M9.75,2.45h-3.5c-1.82,0-3.3,1.48-3.3,3.3v3.5c0,1.82,1.48,3.3,3.3,3.3h3.5 c1.82,0,3.3-1.48,3.3-3.3v-3.5C13.05,3.93,11.57,2.45,9.75,2.45z M11.75,9.25c0,1.105-0.895,2-2,2h-3.5c-1.104,0-2-0.895-2-2v-3.5 c0-1.104,0.896-2,2-2h3.5c1.105,0,2,0.896,2,2V9.25z"></path></svg></h2>

                                        </Col>
                                    </Row>
                                    <br></br>
                                    <h1 className=' ml-2 mb-3'>{campaignDetail?.campaignStatistics?.notDeliveredCount || 0}</h1>
                                    <Row className='ml-1 mr-2'>
                                        <Col span={19}>
                                            <Progress strokeColor={"red"} className="mt-1 mr-1 mb-1 " percent={((campaignDetail?.campaignStatistics?.notDeliveredCount || 0) / (campaignDetail?.campaignStatistics?.receiverCount || 0)) * 100 || 0} showInfo={false} />
                                        </Col>
                                        <Col span={5}>
                                            <h4 className='mt-1 ml-2'>%{campaignDetail?.campaignStatistics?.receiverCount == 0 ? 0 : Math.round(((campaignDetail?.campaignStatistics?.notDeliveredCount || 0) / (campaignDetail?.campaignStatistics?.receiverCount || 1)) * 100).toFixed(0) || 0}</h4>
                                        </Col>
                                    </Row>

                                </Card>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>

                        </Row>
                    </Card>
                    <Card title={t('campaignDetails')} >
                        <div>
                            <Row >
                                <Col className='mt-2' span={12}>
                                    <Descriptions bordered>
                                        <Descriptions.Item label={t('campaignName')} span={3}>{campaignDetail?.campaignDetailInfo?.campaignName}</Descriptions.Item>
                                        <Descriptions.Item label={t('senderNumber')} span={3}>{campaignDetail?.campaignDetailInfo?.sourceNumber}</Descriptions.Item>

                                        <Descriptions.Item label={t('createdDate')} span={3}>
                                            {moment(campaignDetail?.campaignDetailInfo?.createdDate).format('MM-DD-YYYY')} {moment(campaignDetail?.campaignDetailInfo?.createdDate).format('HH:mm:ss')}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('sentGroups')} span={3}>

                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                                {campaignDetail.campaignDetailInfo?.campaignGroupType == 4 ? (
                                                    <Tag className="m-1" color={"blue"}>
                                                        {t("sessionCustomerList")}
                                                    </Tag>
                                                ) : campaignDetail.campaignDetailInfo.sendedGroups?.map((item, index) => (
                                                    <Tag key={index + "groups"} className="m-1" color={"blue"}>
                                                        {item.groupname}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </Descriptions.Item>

                                    </Descriptions>
                                </Col>
                                <Col span={2}>
                                </Col>

                                <Col style={{ background: "#e5ddd5", borderRadius: 5 }} className='mt-1  ml-3' span={8}>
                                    <Row>
                                        <Col span={5}>
                                        </Col>
                                        <Col span={19} className='my-3'>

                                            <div className=" mt-3 mr-2 p-1 " style={{ background: "#d3eebe", borderRadius: 10 }}>

                                                <div className='p-1 ' > {campaignDetail?.campaignDetailInfo?.template?.data}
                                                </div>

                                            </div>
                                            <div className='my-1 '></div>

                                        </Col>

                                    </Row>

                                </Col>
                                <Col span={1}>
                                </Col>
                            </Row>



                        </div>
                    </Card>
                    <Card title={t("recipientInfo")}>
                        <div className="mb-1 row-justify-between" mobileFlex={false} justifyContent='space-between'>
                            <div className="mr-md-3 mb-3" style={{ display: "flex", alignItems: "center", gap: "15px" }}>

                                {/* Search Input */}
                                <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: "bold" }}>{t("search")}</label>
                                    <Input
                                        placeholder={t('searchNameNumber')}
                                        prefix={<SearchOutlined />}
                                        onChange={e => onSearch(e)}
                                    />
                                </div>

                                {/* Status Select */}
                                <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: "bold" }}>{t('status')}</label>
                                    <Select
                                        options={[
                                            { value: 0, label: t("all") },
                                            { value: "read", label: t("read") },
                                            { value: "sent", label: t("sent") },
                                            { value: "delivered", label: t("delivered") },
                                            { value: "failed", label: t("failed") },
                                        ]}
                                        style={{ width: "200px" }}
                                        onChange={(value) => setSelectedStatus(value)}
                                        placeholder={t("selectStatus")}
                                    />
                                </div>

                                {/* Activity Select */}
                                <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: "bold" }}>{t('activity')}</label>
                                    <Select
                                        options={[
                                            { value: 0, label: t("all") },
                                            ...activities.map((item) => ({
                                                value: item.activityid,
                                                label: item.activitydesc,
                                            }))
                                        ]}
                                        style={{ width: "200px" }}
                                        onChange={(value) => setSelectActivity(value)}
                                        placeholder={t("selectActivity")}
                                    />
                                </div>

                                {/* <div>
                                    <label style={{ display: 'block', marginBottom: 4, fontWeight: "bold" }}>{t('groups')}</label>
                                    <Select
                                        options={[
                                            { value: 0, label: t("all") },
                                            ...campaignDetail.campaignDetailInfo.sendedGroups.map((item) => ({
                                                value: item.groupid,
                                                label: item.groupname,
                                            }))
                                        ]}
                                        style={{ width: "200px" }}
                                        onChange={(value) => setSelectGroup(value)}
                                        placeholder={t("selectGroup")}
                                    />
                                </div> */}
                            </div>

                            <div>
                                <ReusableButton
                                    variant="campaign"
                                    text={t('Export')}
                                    onClick={handleExcelExport}
                                    icon={<ExportOutlined />}
                                    className={"mr-2"}

                                />
                            </div>
                        </div>

                        <Col span={24}>
                            <Table
                                size='middle'
                                columns={columns}
                                dataSource={filteredData}
                                rowKey='id'
                            // rowClassName={"custom-row"}
                            /></Col>
                        {/* <Modal
                                open={isModalVisible}
                                onCancel={() => setIsModalVisible(false)}
                                footer={null}
                                title="Müşteri Detayları"
                            >
                                {SelectedCustomer && <EditCustomer param={SelectedCustomer.id} />}
                            </Modal> */}

                        {storedSelectedApp?.appName == "campaign" ? (<>
                            <Modal
                                width={"1200px"}
                                title={t("customerDetail")}
                                open={isModalVisible}
                                footer={null}
                                onCancel={() => setIsModalVisible(false)}
                            >
                                {/* EditCustomer bileşenini burada çağırıyoruz */}
                                <EditCustomer param={modalParam} />
                            </Modal>
                        </>) : (<>
                            <Modal title={t('customerInfoCard')} open={isModalVisible} width={800} onCancel={() => setIsModalVisible(false)} footer={null} >
                                <DetailDashboardCustomer customerId={customerId} />
                            </Modal>
                        </>)}

                        <Modal okText={t("okey")} cancelText={t("cancel")} title={t("addNote")} closable={false} style={{ height: "fit-content" }} closeIcon={false} open={isNoteModal} onCancel={() => setIsNoteModal(false)} onOk={updateActivityNote}>

                            <div>
                                <TextArea
                                    showCount
                                    maxLength={500}
                                    onChange={(event) => setActivityNote(event.target.value)}
                                    placeholder={t("writeNote")}
                                    style={{ height: 230, resize: "none" }}
                                    value={activityNote}
                                />
                            </div>

                        </Modal>

                    </Card>


                </div>
            )
    );

}
/**
 * Maps Redux state to component props.
 *
 * @param {Object} state - Redux root state
 * @returns {{isLoading:boolean,campaignDetail:Object}}
 */
function mapStateToProps(state) {

    return {
        isLoading: state.campaignCustomerReducer.isLoading,
        campaignDetail: state.campaignCustomerReducer.campaignCustomerList,
    }
}

/**
 * Maps dispatch to props for campaign customer selection action.
 *
 * @param {Function} dispatch - Redux dispatch
 * @returns {{selectCampaignCustomersAction:Function}}
 */
function mapDispatchToProps(dispatch) {
    return {
        selectCampaignCustomersAction: bindActionCreators(selectCampaignCustomers, dispatch),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CampaignsDetail);